import { Navigate, Route, Routes } from 'react-router-dom'

import { ProtectedRoute, PublicOnlyRoute } from './auth/guards'
import { AttestationSettings } from './safety/AttestationSettings'
import { FastingGate } from './safety/FastingGate'
import { NotFound } from './pages/NotFound'
import { Onboarding } from './pages/Onboarding'
import { Register } from './pages/Register'
import { SignIn } from './pages/SignIn'
import { Screen } from './pages/Placeholder'
import { Install } from './pages/Install'
import { Pushed } from './pages/Pushed'
import { UiKit } from './pages/UiKit'
import { AppShell } from './ui/AppShell'

/**
 * The route table.
 *
 * Two kinds of route, and the distinction is structural rather than stylistic:
 *
 * - **Tabs** are children of `AppShell`, so they render inside the shell with
 *   the bar below them.
 * - **Full-screen routes are siblings** of the shell. That is how a route
 *   pushes *over* the shell in React Router — the tab bar is not covered, it is
 *   simply not in the subtree. Trying to express this by hiding the bar from
 *   inside the shell is the version that leaks: something always forgets.
 *
 * Tab state surviving a refresh falls out of routing by URL. There is no stored
 * "active tab" to get out of sync. SUAP-0104.
 */
export function App() {
  return (
    <Routes>
      {/* Everything behind the shell needs a session. The guard is a layout
          route rather than a wrapper per screen: a screen added later is
          protected by where it is declared, and forgetting is not an option
          that exists. SUAP-0202. */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
        <Route path="/today" element={<Screen titleKey="screen.today" />} />
        <Route path="/diary" element={<Screen titleKey="screen.diary" />} />
        {/* The fasting tab is behind the safety gate — a layout route, the same
            shape as ProtectedRoute above, so a fasting screen added later is
            gated by *where it is declared* rather than by remembering. The tab
            itself stays in the bar: §3.4 removes the feature, not the person,
            and a tab that vanishes is a person being told something about
            themselves. What they get instead is an explanation. SUAP-1202. */}
        <Route element={<FastingGate />}>
          <Route path="/fasting" element={<Screen titleKey="screen.fasting" />} />
        </Route>
        {/* The Me tab carries standing clinical approvals, because the gate
            screen cannot: it stops rendering the moment an attestation clears
            the gate, taking the withdraw control with it (SUAP-1214). */}
        <Route
          path="/me"
          element={
            <Screen titleKey="screen.me">
              <AttestationSettings />
            </Screen>
          }
        />
        </Route>

        <Route path="/log" element={<Pushed titleKey="screen.log" />} />

        {/* The onboarding wizard is a **sibling** of the shell, not a child: a
            wizard with a tab bar under it is not a wizard, and the six steps are
            a flow the user is meant to finish rather than four places to move
            between. The step travels in the query string (`?step=body`) — see
            pages/Onboarding.tsx for why that is a route-manifest decision rather
            than a taste one. SUAP-0204. */}
        <Route
          path="/onboarding"
          element={
            <Pushed titleKey="screen.onboarding">
              <Onboarding />
            </Pushed>
          }
        />
      </Route>

      {/* Public-only: a signed-in user here is sent on to where they were
          going, or to Today. */}
      <Route element={<PublicOnlyRoute />}>
        <Route
          path="/signin"
          element={
            <Pushed titleKey="screen.signIn">
              <SignIn />
            </Pushed>
          }
        />
        <Route
          path="/register"
          element={
            <Pushed titleKey="screen.register">
              <Register />
            </Pushed>
          }
        />
      </Route>

      {/* Public but not public-only: the landing CTA arrives here with no
          account, and the Me tab links here with one (SUAP-0106). */}
      <Route
        path="/install"
        element={
          <Pushed titleKey="screen.install">
            <Install />
          </Pushed>
        }
      />

      {/* The design-system review surface (SUAP-0103). A sibling route, and a
          real one: `shots` captures it in both themes and `ui:measure` sweeps it
          at every phone width, which a development-only page would never get. */}
      <Route
        path="/ui-kit"
        element={
          <Pushed titleKey="screen.uiKit">
            <UiKit />
          </Pushed>
        }
      />

      <Route path="/" element={<Navigate to="/today" replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}
